/**
 * Подготовка данных для карточки «Порядок пунктов чек-листов» в настройках.
 *
 * Порядок — настройка показа, как переименования: пакет узлов не меняется, а сервер
 * раскладывает пункты в выбранном порядке на тепловой карте, в «По пунктам», в фильтре
 * «Провалы по чек-листам» и в карточке звонка. Каталог отдаёт и эффективный порядок (`items`),
 * и порядок пакета (`defaultItemOrder`) — текущая настройка восстанавливается сравнением,
 * без отдельного запроса.
 *
 * Модулем, а не внутри компонента: вычисление проверяется юнит-тестом, а компонент — e2e.
 */

export const CHECKLIST_KIND = 'checklist_results';

/** Порядок пакета. Каталог старого сервера поля не несёт — тогда отсчёт от текущего порядка. */
export const defaultOrderOf = (chart) =>
  Array.isArray(chart?.defaultItemOrder) && chart.defaultItemOrder.length > 0
    ? chart.defaultItemOrder
    : (chart?.items ?? []).map((item) => item.code);

export const sameOrder = (left = [], right = []) =>
  left.length === right.length && left.every((key, index) => key === right[index]);

/**
 * Чек-лист-карточки каталога в порядке каталога (он же порядок узлов пакета). Скрытые на
 * дашборде тоже здесь: их пункты видны в карточке звонка, и порядок там задаётся этой же
 * настройкой.
 */
export const checklistOrderCharts = (catalog) =>
  (catalog?.charts ?? [])
    .filter((chart) => chart.kind === CHECKLIST_KIND && (chart.items?.length ?? 0) > 1)
    .map((chart) => ({
      id: chart.id,
      title: chart.title,
      hidden: chart.spec?.hidden === true,
      labels: Object.fromEntries(chart.items.map((item) => [item.code, item.label ?? item.code])),
      defaultOrder: defaultOrderOf(chart)
    }));

/** Текущий порядок по каталогу: id карточки → ключи пунктов. */
export const initialItemOrders = (catalog) =>
  Object.fromEntries(
    (catalog?.charts ?? [])
      .filter((chart) => chart.kind === CHECKLIST_KIND)
      .map((chart) => [chart.id, (chart.items ?? []).map((item) => item.code)])
  );

/** Перенос ключа на новую позицию. Новый массив; позиции вне списка — без изменений. */
export const moveKey = (keys, from, to) => {
  if (from === to || from < 0 || to < 0 || from >= keys.length || to >= keys.length) {
    return keys;
  }

  const next = [...keys];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Что отправить на сервер. Совпадение с порядком пакета — это сброс: копия умолчания после
 * перезаливки пакета с новым порядком перебивала бы его.
 */
export const toStoredOrder = (keys, defaultOrder) =>
  sameOrder(keys, defaultOrder) ? null : [...keys];
